/**
 * A helper service used to provide dialogs for various features.
 * Each public function applies different content to the main dialog's md-dialog-content.
 */
export class DialogService {
  private dialogTemplateBasePath = 'app/angularJS/components/dialog/templates/';
  constructor(
    private $mdDialog: ng.material.IDialogService,
    private $translate: ng.translate.ITranslateService,
    private $injector: ng.auto.IInjectorService
  ) { }

  /**
   * Shows a dialog that allows the user to administer an ad-hoc refund
   * @param numberOfRefundsLast28Days the number of refunds that this customer has been given in the last 28 days
   */
  public showAdhocRefundDialog = (numberOfRefundsLast28Days: number) => {
    return this.showDialog(
      this.$translate.instant('ADHOC_REFUND_DIALOG.HEADER'),
      'adhoc-refund-dialog.html',
      {
        numberOfRefundsLast28Days: numberOfRefundsLast28Days
      },
      'adhoc-refund-dialog'
    );
  };

  /**
   * Shows a dialog that allows the user to remove credit from an account
   *
   * @param creditBalance the credit balance of the customer
   */
  public showRemoveCreditDialog = (creditBalance: number) => {
    return this.showDialog(
      this.$translate.instant('REMOVE_CREDIT_DIALOG.HEADER'),
      'remove-credit-dialog.html',
      { creditBalance },
      'remove-credit-dialog'
    );
  };

  /**
 * Shows a dialog that allows the user to refund a balance adjustment
 *
 * @param balanceAdjustment the balance adjustment that we want to refund/partially refund
 */
  public showRefundTopupDialog = (balanceAdjustment: InterACT.Interfaces.ChargeAccounts.BalanceAdjustment, totalRefundAmount: number, isPaymentRequest: boolean) => {
    return this.showDialog(
      this.$translate.instant('REFUND_TOPUP_DIALOG.HEADER'),
      'refund-topup-dialog.html',
      { balanceAdjustment: balanceAdjustment.amount, totalRefundAmount, isPaymentRequest: isPaymentRequest },
      'refund-topup-dialog'
    );
  };

  /**
  * Shows a dialog that confirms if the user wishes to refund
  *
  * @param amount the amount that will be refunded
  * @param originalAmount The original amount of the top-up
  */
  public showRefundTopupConfirmDialog(amount: number, originalAmount: number, totalRefundAmount: number, cardNumber?: string) {
    return this.showDialog(
      this.$translate.instant('REFUND_TOPUP_DIALOG.CONFIRM_HEADER'),
      'refund-topup-confirm-dialog.html',
      { amount, originalAmount, totalRefundAmount, cardNumber },
      'refund-topup-confirm-dialog'
    );
  }

  /**
    * Shows a dialog that allows the user to confirm an ad-hoc refund
    */
  public showConfirmAdhocRefundDialog = (refundDetails) => {
    return this.showDialog(
      this.$translate.instant('CONFIRM_ADHOC_REFUND_DIALOG.HEADER'),
      'confirm-adhoc-refund-dialog.html',
      {
        refundDetails: refundDetails
      },
      'confirm-adhoc-refund-dialog'
    );
  };

  /**
    * Shows a dialog that allows the user to perform an debt recover action
    */
  public showDebtRecoveryDialog = (outstandingDebt: number) => {
    return this.showDialog(
      this.$translate.instant('DEBT_RECOVERY_DIALOG.HEADER'),
      'debt-recovery-dialog.html',
      {
        outstandingDebt: outstandingDebt
      },
      'debt-recovery-dialog'
    );
  };

  /**
  * Shows a dialog that allows the user to perform a top up
  */
  public showTopUpDialog = () => {
    return this.showDialog(
      this.$translate.instant('TOP_UP_DIALOG.HEADER'),
      'top-up-dialog.html',
      {},
      'top-up-dialog'
    );
  };

  /**
  * Shows a dialog that allows the user to add or edit an account name on a charge account
  */
  public showAccountNameDialog = (accountName: string, header: string) => {
    return this.showDialog(
      this.$translate.instant(header),
      'account-name-dialog.html',
      {
        accountName: accountName,
        header: header
      },
      'account-name-dialog'
    );
  };

  /**
   * Shows a dialog that allows the user to perform actions against a charge account
   */
  public showChargeAccountActionsDialog = (actionType: string) => {
    var title;
    var reasons;
    var buttonText;

    if (actionType === 'BLOCK') {
      title = this.$translate.instant('BLOCK_ACCOUNT_DIALOG.BLOCK_ACCOUNT_HEADER');
      buttonText = 'BLOCK_ACCOUNT_DIALOG.BLOCK_ACCOUNT_BUTTON';
      reasons = [
        'LOST_CARD',
        'SUSPICIOUS_ACTIVITY',
        'OTHER'
      ];
    }
    else {
      title = this.$translate.instant('BLOCK_ACCOUNT_DIALOG.UNBLOCK_ACCOUNT_HEADER');
      buttonText = 'BLOCK_ACCOUNT_DIALOG.UNBLOCK_ACCOUNT_BUTTON';
      reasons = [
        'OUTSTANDING_DEBT_SETTLED',
        'PAYMENT_CARD_UPDATED',
        'CUSTOMER_REQUEST',
        'CORRECTING_ERROR'
      ];

    }
    return this.showDialog(
      title,
      'block-account-dialog.html',
      {
        buttonText: buttonText,
        reasons: reasons
      },
      'block-account-dialog'
    );
  };

  /**
   * Shows a dialog that allows the user to create a special day
   * @param specialDay a special day to edit
   * @returns a promise from the showDialog method
   */
  public showSpecialDayDialog = (specialDay: InterACT.Interfaces.SpecialDays.SpecialDay) => {
    const specialDayDialogController: any = this.$injector.get('$controller')('SpecialDayDialogController', { $scope: {} });
    if (specialDay.id) {
      specialDay.date = <any>new Date(specialDay.date);
    }
    specialDayDialogController.specialDay = specialDay;

    return this.showDialog(
      this.$translate.instant(specialDay.id ? 'SPECIAL_DAY_DIALOG.EDIT_SPECIAL_DAY' : 'SPECIAL_DAY_DIALOG.CREATE_SPECIAL_DAY'),
      'special-days-dialog.html',
      {
        specialDayDialogController: specialDayDialogController,
      },
      'special-day-dialog'
    );
  };

  /**
 * Shows a dialog that allows the user to create a usage rating rule
 * @returns a promise from the showDialog method
 */
  public showUsageRatingDialog = (usageRatingRule?: InterACT.Interfaces.JourneyConstructionRules.JourneyConstructionRule) => {
    return this.showDialog(
      this.$translate.instant(usageRatingRule ? 'EDIT_USAGE_RATING_RULE' : 'CREATE_USAGE_RATING_RULE'),
      'best-value-calculation.html',
      {
        usageRatingRule: usageRatingRule,
        isRatingFlowSelection: (usageRatingRuleType: string) => {
          return usageRatingRuleType === 'RATING_FLOW_SELECTION'
        },
        ruleTypes: [
          'BEST_VALUE_CALCULATION',
          'OPERATIONAL_DAY',
          'RATING_FLOW_SELECTION'
        ],
        bestValueCalculationOptions: [
          'ROLLING_CAPS',
          'PERIOD_PRODUCTS',
          'RAIL_FARES_SERVICE'
        ]
      },
      'best-value-calculation-dialog'
    );
  };

  /**
   * Shows a dialog that allows the user to create a usage event
   * @param usageEvent a usage event to edit
   * @returns a promise from the showDialog method
   */
  public showUsageEventManagementDialog = (usageEvent, correctionCount) => {
    const usageEventManagementDialogController: any = this.$injector.get('$controller')('UsageEventManagementDialogController', { $scope: {} });

    usageEventManagementDialogController.usageEvent = usageEvent;
    usageEventManagementDialogController.correctionCount = correctionCount;
    if (usageEvent.id) {
      usageEventManagementDialogController.populateData();
    }

    return this.showDialog(
      this.$translate.instant(usageEvent.id ? 'USAGE_EVENT_MANAGEMENT_DIALOG.EDIT_USAGE_EVENT' : 'USAGE_EVENT_MANAGEMENT_DIALOG.CREATE_USAGE_EVENT'),
      'usage-event-management-dialog.html',
      {
        usageEventManagementDialogController: usageEventManagementDialogController,
      },
      'usage-event-management-dialog'
    );
  };

  /**
   * Shows a dialog holding the widget library where the user can pick a new widget to display
   * @returns a promise from the showDialog method
   */
  public showWidgetLibrary = (): ng.IPromise<Array<string>> => {
    return this.showDialog(
      this.$translate.instant('DASHBOARD.WIDGET_LIBRARY'),
      'widget-library-dialog.html',
      { searchHeader: true },
      'widget-library-dialog'
    );
  }

  /**
   * Shows a dialog holding the custom widget creator where the user can create a new widget to display
   * @returns a promise from the showDialog method
   */
  public showWidgetCreator = (): ng.IPromise<InterACT.Interfaces.IVisualisation> => {
    return this.showDialog(
      this.$translate.instant('DASHBOARD.CUSTOM_WIDGET'),
      'custom-widget-creator-dialog.html',
      {},
      'custom-widget-creator-dialog'
    );
  }

  /**
   * Shows a dialog displaying the audit history for a resource
   * @param resourceLink the link to the resource to get audit history for
   * @param title the title for the dialog
   * @returns a promise from the showDialog method
   */
  public showResourceAuditHistoryDialog = (resourceLink: string, title: string): ng.IPromise<File> => {
    return this.showDialog(
      title,
      'resource-audit-history-dialog.html',
      { resourceLink: resourceLink },
      'resource-audit-history-dialog'
    );
  }

  /**
   * Shows a dialog allowing the import of a file
   * @param title the title for the dialog
   * @param fileExtensions an array of file extensions to allow import of
   * @returns a promise from the showDialog method
   */
  public showImportFileDialog = (title: string, fileExtensions: Array<string>) => {
    return this.showDialog(
      title,
      'import-file-dialog.html',
      { allowedFileTypes: fileExtensions.join(','), importDetail: {} },
      'import-file-dialog'
    );
  }

  /**
   * Shows a dialog to capture the reason for a change in a data object
   */
  public showAuditReasonDialog = (): ng.IPromise<string> => {
    return this.showDialog(
      this.$translate.instant('REASON'),
      'audit-reason-dialog.html',
      { reason: undefined },
      'audit-reason-dialog'
    );
  }

  /**
   * Shows a dialog displaying the audit history for a resource
   * @param dialogTitle the title for the dialog
   * @param content a path to the content to include in the dialog html
   * @param options a list of options to pass to the dialog controller, used to pass data from here to the dialog content.
   * @param dialogClass the class to set on the md-dialog element, typically to adjust dialog width
   * @returns a promise from the dialog service, this is returned on close of dialog
   */
  public showDialog = (dialogTitle: string, content: string, options: Object, dialogClass: string) => {
    return this.$mdDialog.show({
      templateUrl: 'app/angularJS/components/dialog/dialog.html',
      clickOutsideToClose: true,
      escapeToClose: true,
      controller: 'DialogController',
      controllerAs: 'vm',
      bindToController: true,
      locals: {
        options: options,
        dialogClass: dialogClass,
        dialogTitle: dialogTitle,
        content: `${ this.dialogTemplateBasePath }${ content }`
      }
    });
  }
}
